import type { RoomResponse } from '../types/auth';

/**
 * Drop rooms that must not appear on a public discovery surface (starting-soon,
 * live-now, home strips) before showing a list. Owner-facing surfaces (own
 * studio, own dashboard) call different endpoints and don't go through this
 * helper.
 *
 * A room is dropped when EITHER of the following holds:
 *  - legacy `isPublic === false`
 *  - CORE-541 `visibility === 'Private'` (three-state audience that supersedes
 *    `isPublic` on the unified Live-session)
 *
 * ── Why NOT `creatorRole` (CORE-589 UPD, 04.08.2026) ────────────────────────
 *
 * Until this UPD the filter also dropped `creatorRole === 'user'`, on the
 * assumption that every regular-user session is private by definition. The
 * capability/visibility model (CORE-768 §3, «следствие visibility») removes
 * that assumption: a regular user at «Проводник света» may open a Subscribers
 * session and at «Магистр света» a Public one, and such a session is a
 * legitimate discovery result. External display must key off the session's
 * ACTUAL visibility/configuration, never off the creator's role (CORE-769 §0
 * запрет №1 — «право никогда не выводится из creatorRole»).
 *
 * This is safe because the audience gate is enforced server-side, not here:
 * the discovery endpoints these strips read (`GetActivePublicRoomsAsync` /
 * `GetUpcomingPublicRoomsAsync`, and the calendar's `WhereVisibleTo`) already
 * return only `Visibility == Public` / subscriber-visible rooms, with NO role
 * predicate. A private user session therefore carries `visibility === 'Private'`
 * (or `isPublic === false`) and is dropped by its own audience field — which is
 * exactly «Приватная пользовательская Live-сессия нигде публично не
 * отображается», expressed through the session's configuration rather than a
 * client-side role rule.
 *
 * Defensive: a room with neither field set (legacy room, `isPublic` true and
 * the unified fields `undefined`) is kept — a schema change that relaxes the
 * type must not accidentally hide legitimate public rooms.
 */
export function filterPublicRooms(rooms: RoomResponse[]): RoomResponse[] {
  return rooms.filter((r) => r.isPublic !== false && r.visibility !== 'Private');
}
